import { withSiteContext } from '../../interceptors/site-context.interceptor';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiStoreTemplates } from '../../enums/api-store-templates.enum';
import { GenericConstants } from '../../enums/generic-constants.enum';
import { TemplateConfig } from '../../models/store-templates/template-config';
import { TemplateMenusRQ } from '../../models/store-templates/template-menus-rs';
import { SelectedTemplate } from '../../models/store-templates/template-site';

@Injectable({
  providedIn: 'root'
})
export class TemplatesService {

  /** Permiso de vista previa por tienda; dura 12 h en el backend, se renueva antes. */
  private previewTokens = new Map<string, { at: number; token$: Observable<string> }>();

  constructor(private http: HttpClient) { }

  /**
   * Permiso firmado para ver el BORRADOR en el storefront (`preview-token` en la URL de la vista
   * previa). Sin él, el storefront enseña lo publicado.
   */
  previewToken(siteId: string): Observable<string> {
    const cached = this.previewTokens.get(siteId);
    if (cached && Date.now() - cached.at < 10 * 3600_000) return cached.token$;
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    const token$ = this.http.post<{ token: string }>(`${environment.baseUrl}store/admin/store-template/${siteId}/preview-token`, {},
      { headers, context: withSiteContext(siteId) }).pipe(
        map(response => response.token),
        // Un fallo no se queda en caché: el siguiente intento vuelve a pedirlo.
        catchError(error => { this.previewTokens.delete(siteId); return throwError(() => error); }),
        shareReplay(1));
    this.previewTokens.set(siteId, {at: Date.now(), token$});
    return token$;
  }

  /** La URL de la vista previa del borrador, con su permiso. */
  previewUrl(base: string, path: string, token: string, previewId: string): string {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    return `${normalizedBase}${path}?template-status=DRAFT&preview-token=${encodeURIComponent(token)}&preview-id=${previewId}`;
  }

  getStoreTemplate(siteId: string, templateId: string = "") {
    let url = environment.baseUrl + ApiStoreTemplates.STORE_TEMPLATE + siteId + '/draft';
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    let urlencoded = new HttpParams()
      .set("templateBaseId", templateId);
    return this.http.get(url, { params: urlencoded, headers: headers, context: withSiteContext(siteId) });
  }

  //update template
  saveDraftTemplate(siteId: string, template: TemplateConfig) {
    let url = environment.baseUrl + ApiStoreTemplates.STORE_TEMPLATE + siteId + '/draft';
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.put(url, JSON.stringify(template), { headers: headers, context: withSiteContext(siteId) });
  }
  //ADMIN_STORE
  //Get menus
  getTemplateMenus(siteId: string, templateId: string = ""){
    let url = environment.baseUrl + ApiStoreTemplates.ADMIN_STORE + ApiStoreTemplates.MENUS + siteId + '/draft';
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    let urlencoded = new HttpParams()
      .set("templateBaseId", templateId);
    return this.http.get(url, { params: urlencoded, headers: headers, context: withSiteContext(siteId) });
  }

  saveDraftMenus(siteId: string, templateMenus: TemplateMenusRQ) {
    let url = environment.baseUrl + ApiStoreTemplates.ADMIN_STORE + ApiStoreTemplates.MENUS + siteId + '/draft';
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.put(url, JSON.stringify(templateMenus), { headers: headers, context: withSiteContext(siteId) });
  }

  publishTemplate(siteId:string, templateAdminId:string){
    let url = environment.baseUrl + ApiStoreTemplates.ADMIN_STORE + siteId +'/' + templateAdminId + ApiStoreTemplates.GO_LIVE;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post(url,"" ,{ headers: headers, context: withSiteContext(siteId) });
  }

  publishThemeWeaverTemplate(siteId: string, templateAdminId: string) {
    const url = environment.baseUrl + ApiStoreTemplates.STORE_TEMPLATE
      + siteId + '/draft/theme-weaver/' + templateAdminId + ApiStoreTemplates.GO_LIVE;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post(url, '', { headers, context: withSiteContext(siteId) });
  }

  //*--*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*//
  getTemplateBase(siteId:string){
    let url = environment.baseUrl + ApiStoreTemplates.STORE_UI_TEMPLATE  + siteId + ApiStoreTemplates.STORE_TEMPLATE_BASE;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.get(url, { headers: headers, context: withSiteContext(siteId) });
  }

  scrollTo(siteId:string, templateId:string, sectionId:string){
    let url = environment.baseUrl + ApiStoreTemplates.ADMIN_STORE + siteId + ApiStoreTemplates.ADMIN_TEMPLATE + templateId + ApiStoreTemplates.SCROLL_TEMPLATE + sectionId;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post(url,"" ,{ headers: headers, context: withSiteContext(siteId) });
  }

  saveThemBasetemplate(siteId:string, templateId:string, themeId:string){
    let url = environment.baseUrl + ApiStoreTemplates.ADMIN_STORE  + siteId + ApiStoreTemplates.STORE_TEMPLATE_BASE +"/"+ templateId + ApiStoreTemplates.THEME + themeId + "/draft";
      let headers = new HttpHeaders({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
      });
      return this.http.patch(url,"",{ headers: headers, context: withSiteContext(siteId) })
  }

  selectTemplate(selectedTemplate:SelectedTemplate){
    let url = environment.baseUrl + ApiStoreTemplates.STORE_UI_TEMPLATE  +  ApiStoreTemplates.SELECT_TEMPLATE;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });

    return this.http.post(url, selectedTemplate, { headers: headers, context: withSiteContext(selectedTemplate.site_id) });
  }

  generateAuthCode(siteId: string): Observable<{ code: string; expires_in: number }> {
    let url = environment.baseUrl + 'admin/auth/code';
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      Authorization: localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN),
    });
    return this.http.post<{ code: string; expires_in: number }>(url, { site_id: siteId }, { headers: headers, context: withSiteContext(siteId) });
  }
}
